import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pin, Trash2, Loader2, FolderOpen, Download } from 'lucide-react';
import { toast } from 'sonner';
import SeverityBadge from '@/components/apex/SeverityBadge';
import { FrameworkBadge } from '@/components/apex/Badges';

export default function AuditBundles() {
  const { API, seed } = useApp();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [framework, setFramework] = useState('SOC 2');
  const [note, setNote] = useState('');

  const reload = useCallback(() => {
    axios.get(`${API}/audit-bundles`).then((r) => {
      setBundles(r.data.items);
      setLoading(false);
    });
  }, [API]);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!name.trim()) { toast.error('Bundle name required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/audit-bundles`, { name: name.trim(), framework, note: note || null, initial_packets: [] });
      toast.success('Bundle created');
      setName(''); setNote(''); setShowForm(false);
      reload();
    } finally { setCreating(false); }
  };

  const unpin = async (bundleId, investigationId) => {
    await axios.delete(`${API}/audit-bundles/${bundleId}/pins/${investigationId}`);
    toast.success('Removed from bundle');
    reload();
  };

  const remove = async (bundleId) => {
    await axios.delete(`${API}/audit-bundles/${bundleId}`);
    toast.success('Bundle deleted');
    reload();
  };

  const downloadPacket = (id, format) => {
    const a = document.createElement('a');
    a.href = `${API}/evidence-packets/${id}/${format}`;
    a.download = `apex-packet-${id}.${format}`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="audit-bundles">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-apex-ink">My Audit Bundles</h3>
          <p className="text-[11px] text-apex-neutral mt-0.5">Pin evidence packets to a custom bundle for your audit cycle.</p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)} variant="outline" size="sm" data-testid="bundle-toggle-form" className="gap-1.5 text-xs">
          <Plus className="w-3.5 h-3.5" />
          New bundle
        </Button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Name</Label>
                  <Input data-testid="bundle-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="SOC 2 CC6.1 Q2 cycle" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Framework</Label>
                  <Select value={framework} onValueChange={setFramework}>
                    <SelectTrigger data-testid="bundle-framework"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(seed?.audit_workspace?.frameworks ?? []).map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Note (optional)</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Auditor: External Audit Co. · Cycle: Q2 2026" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button onClick={create} disabled={creating} data-testid="bundle-create" size="sm" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
                  {creating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Creating</> : <><Plus className="w-3.5 h-3.5" />Create</>}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="text-xs text-apex-neutral inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</div>
      ) : bundles.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-6 text-center">
          <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" strokeWidth={1.4} />
          <p className="text-sm font-semibold text-apex-ink">No audit bundles yet</p>
          <p className="text-xs text-apex-neutral mt-1">Create one above, then pin packets from Evidence Vault or Investigations.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bundles.map((b) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`bundle-${b.id}`}
              className="border border-slate-200 rounded-md p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-apex-neutral">{b.id}</span>
                    <FrameworkBadge framework={b.framework} />
                  </div>
                  <h4 className="text-sm font-semibold text-apex-ink">{b.name}</h4>
                  {b.note && <p className="text-[11px] text-apex-neutral mt-0.5">{b.note}</p>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(b.id)} data-testid={`bundle-delete-${b.id}`} className="text-apex-critical hover:text-apex-critical hover:bg-red-50 gap-1 text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </Button>
              </div>

              {b.pins.length === 0 ? (
                <p className="text-xs text-apex-neutral italic">No packets pinned yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {b.pins.map((p) => (
                    <div key={p.investigation_id} className="flex items-center justify-between gap-2 bg-slate-50 rounded px-3 py-2 border border-slate-200">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Pin className="w-3 h-3 text-apex-lens shrink-0" />
                        <SeverityBadge severity={p.severity} size="sm" icon={false} />
                        <span className="text-[12px] font-semibold truncate">{p.title}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => downloadPacket(p.investigation_id, 'pdf')} className="text-[10px] uppercase tracking-wider text-apex-lens font-semibold inline-flex items-center gap-0.5 px-2 py-1 rounded hover:bg-white">
                          <Download className="w-3 h-3" />PDF
                        </button>
                        <button onClick={() => unpin(b.id, p.investigation_id)} data-testid={`bundle-unpin-${b.id}-${p.investigation_id}`} className="text-[10px] text-apex-neutral hover:text-apex-critical p-1">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 text-[10px] font-mono text-apex-neutral">
                {b.pins.length} packet{b.pins.length !== 1 ? 's' : ''} · Updated {new Date(b.updated_at).toLocaleDateString()}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
